import {Part} from 'biojs/Part';
import * as BiojsUtil from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import Protein from 'molbio/aa-sequences/AaSequence';
import Annotation from 'molbio/annotations/backbone/Annotation';
import * as CodonOptimizationTabReduxActions from 'molbio/codon-optimization/CodonOptimizationTabReduxActions';
import {doesAnnotationOverlapRegion} from 'molbio/codon-optimization/CodonOptimizationUtil';
import * as sequtil from 'molbio/SeqUtil';
import {PolymerTabState, PolymerTabStateAttributes} from 'molbio/tabstates/PolymerTabState';
import {TabStateDefaults} from 'Navigation';
import {getStore} from 'redux-stores/Store';

interface CodonOptimizationTabStateAttributes extends PolymerTabStateAttributes<Sequence | Protein> {
  strand: StrandOfDoubleStranded; // Only set for Sequence-backed tabs.
  bases: string; // Only set for Protein-backed tabs.
}

interface CodonOptimizationTabState {
  get<K extends keyof CodonOptimizationTabStateAttributes>(key: K): CodonOptimizationTabStateAttributes[K];
  set<K extends keyof CodonOptimizationTabStateAttributes>(
    attributeName: K,
    value: CodonOptimizationTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): CodonOptimizationTabState;
  set(obj: Partial<CodonOptimizationTabStateAttributes>): CodonOptimizationTabState;
}

class CodonOptimizationTabState extends PolymerTabState<Sequence | Protein> {
  override initialize(...args: Array<unknown>): void {
    super.initialize(args);
    const tabId = this.cid;
    getStore().dispatch(CodonOptimizationTabReduxActions.newTab(tabId));
    const polymer = this.polymer();
    const selection = polymer.selection().assertNotEmpty();

    if (polymer instanceof Sequence) {
      let bases;
      if (this.get('strand') === 1) {
        bases = polymer.residueSlice(selection.start(), selection.end());
      } else {
        bases = BiojsUtil.revcomp(
          polymer.residueSlice(selection.start(), selection.end()),
          polymer._sequence().baseTable()
        );
      }
      getStore().dispatch(
        CodonOptimizationTabReduxActions.setSelection(
          tabId,
          selection.start(),
          selection.end(),
          bases,
          polymer.length() === selection.length(),
          this.get('strand')
        )
      );
      polymer
        .parts()
        .getSequencePartsBetween(selection.start(), selection.end())
        .filter(
          (part: Part) => !sequtil.isRangeWithin(selection.start(), selection.end(), part.start(), part.end())
        )
        .forEach((part: Part) => {
          getStore().dispatch(
            CodonOptimizationTabReduxActions.addProtectedRegion(tabId, {
              start: part.start(),
              end: part.end(),
              name: part.get('sequence').get('name'),
            })
          );
        });
      polymer
        .get('annotations')
        .filter((annotation: Annotation) =>
          doesAnnotationOverlapRegion(
            annotation.start(),
            annotation.end(),
            selection.start(),
            selection.end()
          )
        )
        .filter(
          (annotation: Annotation) =>
            !sequtil.isRangeWithin(selection.start(), selection.end(), annotation.start(), annotation.end())
        )
        .forEach((annotation: Annotation) => {
          getStore().dispatch(
            CodonOptimizationTabReduxActions.addProtectedRegion(tabId, {
              start: annotation.start(),
              end: annotation.end(),
              name: annotation.name(),
            })
          );
        });
    } else if (polymer instanceof Protein) {
      getStore().dispatch(
        CodonOptimizationTabReduxActions.setSelection(
          tabId,
          selection.start(),
          selection.end(),
          this.get('bases'),
          polymer.length() === selection.length(),
          0
        )
      );
    }
  }

  override defaults(): TabStateDefaults {
    return {
      ...super.defaults(),
      title: 'Codon Optimization',
    };
  }

  override close(): void {
    super.close();
    getStore().dispatch(CodonOptimizationTabReduxActions.closeOptimization(this.cid));
  }
}

export default CodonOptimizationTabState;
